import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import styles from './AdDetail.module.css';
import tStyles from './Table.module.css';

const SERVER_URL =
  process.env.NODE_ENV === 'production'
    ? window.location.origin
    : 'http://localhost:3000';
const imgUrl = (p) => (p ? `${SERVER_URL}${p.startsWith('/') ? p : `/${p}`}` : '');

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ads, setAds] = useState([]);
  const [payments, setPayments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pkgId, setPkgId] = useState('');
  const [pkgMsg, setPkgMsg] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/users/${id}`),
      api.get('/packages'),
    ])
      .then(([uRes, pRes]) => {
        setUser(uRes.data.user);
        setAds(uRes.data.ads || []);
        setPayments(uRes.data.payments || []);
        setPackages((pRes.data.packages || []).filter((p) => p.isActive));
        setPkgId(uRes.data.user.activePackage?._id || '');
      })
      .catch(() => alert('Failed to load user'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line

  const toggle = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${id}/toggle`);
      setUser((prev) => ({ ...prev, isActive: !prev.isActive }));
    } catch {
      alert('Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const label = user?.name || user?.phone || user?.email || 'this user';
    if (
      !window.confirm(
        `Delete ${label}? Their ads and chats will also be removed. This cannot be undone.`
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await api.delete(`/admin/users/${id}`);
      navigate('/users');
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
      setSaving(false);
    }
  };

  const assignPackage = async () => {
    setSaving(true);
    setPkgMsg('');
    try {
      const { data } = await api.patch(`/admin/users/${id}/package`, {
        packageId: pkgId || null,
      });
      setUser((prev) => ({
        ...prev,
        activePackage: data.activePackage,
        packageExpiresAt: data.packageExpiresAt,
        packageAssignedAt: data.packageAssignedAt,
        bumpCreditsRemaining: data.bumpCreditsRemaining,
      }));
      setPkgMsg(pkgId ? 'Package assigned' : 'Package cleared');
      setTimeout(() => setPkgMsg(''), 2500);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update package');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.center}>Loading...</div>;
  if (!user) return <div className={styles.center}>User not found.</div>;

  const initials = (user.name || user.email || '?')[0].toUpperCase();
  const pkgActive =
    user.activePackage &&
    (!user.packageExpiresAt || new Date(user.packageExpiresAt) > new Date());

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => navigate('/users')}>
        ← Back to Users
      </button>

      <div className={styles.layout}>
        <div className={styles.left}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            {user.avatar ? (
              <img
                src={imgUrl(user.avatar)}
                alt=""
                style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: '#e8f5e9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 40,
                  fontWeight: 700,
                  color: '#6CA651',
                }}
              >
                {initials}
              </div>
            )}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{user.name || '—'}</div>
              <div style={{ color: '#888', fontSize: 13 }}>{user.email}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <span className={user.isActive ? tStyles.badgeActive : tStyles.badgeInactive}>
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
              {user.isBlocked && (
                <span
                  style={{
                    background: '#ffeaea',
                    color: '#cc0000',
                    padding: '2px 10px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Blocked
                </span>
              )}
              {pkgActive && (
                <span className={tStyles.badgeFeatured}>
                  {user.activePackage?.name || 'Package'}
                </span>
              )}
            </div>
          </div>

          <div className={styles.actions} style={{ marginTop: 20 }}>
            <button
              className={user.isActive ? styles.disapproveBtn : styles.approveBtn}
              disabled={saving}
              onClick={toggle}
            >
              {user.isActive ? '⏸ Deactivate' : '▶ Activate'}
            </button>
            <button
              className={styles.deleteBtn}
              disabled={saving}
              onClick={handleDelete}
            >
              🗑 Delete User
            </button>
          </div>
        </div>

        <div className={styles.right}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>User Details</div>
            <div className={styles.grid}>
              <Row label="Phone" value={user.phone} />
              <Row label="Address" value={user.address} />
              <Row label="City" value={user.city} />
              <Row label="State" value={user.state} />
              <Row label="ZIP" value={user.zip} />
              <Row
                label="Joined"
                value={new Date(user.createdAt).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              />
              <Row label="Total Ads" value={String(ads.length)} />
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Seller Package</div>
            <p style={{ fontSize: 12, color: '#888', marginTop: 0 }}>
              Ads posted while this package is active expire after the package&apos;s{' '}
              <strong>duration days</strong>. Without a package, App Settings expiry is used.
            </p>
            {user.activePackage ? (
              <div className={styles.grid} style={{ marginBottom: 12 }}>
                <Row label="Current plan" value={user.activePackage.name} />
                <Row
                  label="Price"
                  value={
                    user.activePackage.price != null
                      ? `₹${Number(user.activePackage.price).toLocaleString('en-IN')}`
                      : null
                  }
                />
                <Row
                  label="Ad expiry"
                  value={`${user.activePackage.durationDays} days per ad`}
                />
                <Row
                  label="Package ends"
                  value={
                    user.packageExpiresAt
                      ? new Date(user.packageExpiresAt).toLocaleString()
                      : '—'
                  }
                />
                <Row
                  label="Assigned at"
                  value={
                    user.packageAssignedAt
                      ? new Date(user.packageAssignedAt).toLocaleString()
                      : null
                  }
                />
                <Row
                  label="Bump credits"
                  value={String(user.bumpCreditsRemaining ?? 0)}
                />
                <Row
                  label="Max ads"
                  value={
                    user.activePackage.maxActiveAds === 0
                      ? 'Unlimited'
                      : String(user.activePackage.maxActiveAds)
                  }
                />
                <Row
                  label="Status"
                  value={pkgActive ? 'Active' : 'Expired / inactive'}
                />
              </div>
            ) : (
              <p style={{ color: '#aaa', fontSize: 13, marginTop: 0 }}>
                No active package. User is on App Settings limits.
              </p>
            )}
            {pkgMsg && (
              <div
                style={{
                  background: '#e8f8ef',
                  color: '#1a9e5c',
                  padding: '8px 12px',
                  borderRadius: 8,
                  marginBottom: 10,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {pkgMsg}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={pkgId}
                onChange={(e) => setPkgId(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: 180,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 14,
                }}
              >
                <option value="">No package (use App Settings)</option>
                {packages.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name} — {p.durationDays}d ads · ₹{p.price}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={styles.saveMsgBtn}
                disabled={saving}
                onClick={assignPackage}
              >
                {pkgId ? 'Assign package' : 'Clear package'}
              </button>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              Purchase history ({payments.length})
            </div>
            {payments.length === 0 ? (
              <p style={{ color: '#aaa', fontSize: 13 }}>No package purchases yet.</p>
            ) : (
              <div className={tStyles.tableWrap}>
                <table className={tStyles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Package</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Payment ID</th>
                      <th>Order ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p._id}>
                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {new Date(p.createdAt).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td style={{ fontWeight: 600 }}>
                          {p.package?.name || '—'}
                        </td>
                        <td>
                          {p.amount > 0
                            ? `₹${Number(p.amount).toLocaleString('en-IN')}`
                            : 'Free'}
                        </td>
                        <td>
                          <span
                            className={
                              p.status === 'paid'
                                ? tStyles.badgeActive
                                : p.status === 'failed'
                                  ? tStyles.badgeInactive
                                  : tStyles.badgePending
                            }
                          >
                            {p.status}
                          </span>
                        </td>
                        <td style={{ fontSize: 11, color: '#666', wordBreak: 'break-all' }}>
                          {p.razorpayPaymentId || '—'}
                        </td>
                        <td style={{ fontSize: 11, color: '#666', wordBreak: 'break-all' }}>
                          {p.razorpayOrderId || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Ads ({ads.length})</div>
            {ads.length === 0 ? (
              <p style={{ color: '#aaa', fontSize: 13 }}>No ads posted yet.</p>
            ) : (
              <div className={tStyles.tableWrap}>
                <table className={tStyles.table}>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Title</th>
                      <th>Price</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Approved</th>
                      <th>Expires</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ads.map((ad) => (
                      <tr
                        key={ad._id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/ads/${ad._id}`)}
                      >
                        <td>
                          {ad.images?.[0] ? (
                            <img src={imgUrl(ad.images[0])} alt="" className={tStyles.thumb} />
                          ) : (
                            <span className={tStyles.noImg}>—</span>
                          )}
                        </td>
                        <td style={{ color: '#6c63ff', fontWeight: 500 }}>{ad.title}</td>
                        <td>₹{Number(ad.price).toLocaleString()}</td>
                        <td>{ad.category?.name || '—'}</td>
                        <td>
                          <span
                            className={
                              ad.isActive ? tStyles.badgeActive : tStyles.badgeInactive
                            }
                          >
                            {ad.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={
                              ad.approvalStatus === 'approved'
                                ? tStyles.badgeActive
                                : tStyles.badgeInactive
                            }
                          >
                            {ad.approvalStatus}
                          </span>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {ad.expiresAt
                            ? new Date(ad.expiresAt).toLocaleDateString()
                            : '—'}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {new Date(ad.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{value}</span>
    </div>
  );
}
